/**
 * Shared positioning helpers for this table family's portal-rendered hover
 * popovers - originally written for ReleaseMomentDateCell's DateTooltip and
 * extracted here so ApproverCell's own approver-list popover (and any
 * future one) reuses the exact same technique instead of a second
 * implementation.
 */

/**
 * Above ordinary page content (this codebase's own contextual-popover tier
 * - see the repeated `z-index: 9999`/`10000` values used elsewhere, e.g.
 * dropdowns/menus), but comfortably below SlideOutPanel's modal-level
 * `z-index: 100001` (index.scss) so the RSD slide-out still wins if both
 * are ever visible at once.
 */
export const POPOVER_PORTAL_Z_INDEX = 10000;

/**
 * Same portal target SlideOutPanel already uses (see
 * `SlideOutPanel/index.tsx`'s `createPortal(content, document.querySelector(
 * '[data-omega-feature="shell header"]'))`, with a comment showing they
 * moved *away* from `document.body` for the same reason this needs to).
 *
 * Root cause of a portaled popover rendering with a transparent/see-through
 * background (table row text visible "through" it, regardless of z-index):
 * Spectrum's own CSS custom properties (`var(--spectrum-global-color-
 * gray-50)`, etc.) are *defined* on the `<Provider>` component's own root
 * DOM node (src/app/index.tsx) and inherited via normal CSS custom-property
 * inheritance down the *DOM* tree - not via React context, which portals
 * don't affect. `document.body` is an ANCESTOR of that Provider's DOM
 * output, not a descendant, so content portaled there falls outside the
 * DOM subtree those variables are defined on and resolves to their
 * (transparent) initial values. `[data-omega-feature="shell header"]` is
 * the `spectrum-ShellContainer` div rendered *inside* both top-level
 * `<Provider>`s (src/app/index.tsx), so portaling here keeps the same CSS
 * custom properties in scope - this is a real DOM-inheritance bug, not a
 * z-index/stacking-context one; raising z-index alone could not fix it.
 */
export function getTooltipPortalTarget(): Element {
  return (
    document.querySelector('[data-omega-feature="shell header"]') ||
    document.body
  );
}

/**
 * Same relative-to-scroll-container approach as Roadmap's own
 * calculateVerticalPosition (TimelineCard.tsx): compare space above vs.
 * below the trigger against the page's actual scrollable content area,
 * not the raw window, and open on whichever side has more room. Here that
 * area is `.content-container` (see ReviewApproval/index.tsx's
 * `data-testid="review-approval-content-area"`) - the one element that
 * actually scrolls on this page (the body scroll is intentionally locked;
 * see index.tsx's own comment on that). Falls back to the viewport if the
 * container isn't found, matching Roadmap's own fallback.
 */
export function calculateVerticalPlacement(
  anchorRect: DOMRect,
  safetyMargin = 20
): 'top' | 'bottom' {
  const container = document.querySelector(
    '[data-testid="review-approval-content-area"]'
  );
  const containerRect = container?.getBoundingClientRect();
  const containerTop = containerRect?.top ?? 0;
  const containerBottom = containerRect?.bottom ?? window.innerHeight;

  const spaceBelow = containerBottom - anchorRect.bottom - safetyMargin;
  const spaceAbove = anchorRect.top - containerTop - safetyMargin;

  return spaceAbove > spaceBelow ? 'top' : 'bottom';
}
